/**
 * Generates reference audio tracks using Web Audio API synthesis.
 * Each test gets a distinct musical loop rendered offline, returned as a blob URL.
 */

type TrackKey = "lock_in" | "improvisation" | "follow_direction";

const cache = new Map<TrackKey, string>();

function renderOffline(
  durationSec: number,
  buildGraph: (ctx: OfflineAudioContext) => void
): Promise<Blob> {
  const sampleRate = 44100;
  const ctx = new OfflineAudioContext(2, sampleRate * durationSec, sampleRate);
  buildGraph(ctx);
  return ctx.startRendering().then((buffer) => {
    const wav = audioBufferToWav(buffer);
    return new Blob([wav], { type: "audio/wav" });
  });
}

/* ---------- Track A: "Just the Two of Us" style (Dbmaj7–C7–Fm7–Ebm7–Ab7) ---------- */
function buildLockIn(ctx: OfflineAudioContext) {
  const bpm = 98;
  const beatDur = 60 / bpm;
  const chords = [
    [277.18, 349.23, 415.30], // Dbmaj7
    [261.63, 329.63, 392.00], // C7
    [174.61, 220.00, 261.63], // Fm7
    [155.56, 196.00, 233.08], // Ebm7→Ab7
  ];
  const melodyNotes = [
    349.23, 415.30, 466.16, 415.30,
    392.00, 349.23, 329.63, 311.13,
    349.23, 415.30, 466.16, 523.25,
    466.16, 415.30, 349.23, 311.13,
  ];
  const totalBeats = Math.floor(ctx.length / ctx.sampleRate / beatDur);

  for (let beat = 0; beat < totalBeats; beat++) {
    const chord = chords[Math.floor(beat / 4) % chords.length];
    const startTime = beat * beatDur;

    chord.forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.06, startTime + 0.05);
      gain.gain.linearRampToValueAtTime(0.04, startTime + beatDur * 0.8);
      gain.gain.linearRampToValueAtTime(0, startTime + beatDur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + beatDur + 0.05);
    });

    const bassOsc = ctx.createOscillator();
    const bassGain = ctx.createGain();
    bassOsc.type = "triangle";
    bassOsc.frequency.value = chord[0] / 2;
    bassGain.gain.setValueAtTime(0.12, startTime);
    bassGain.gain.exponentialRampToValueAtTime(0.01, startTime + beatDur * 0.8);
    bassOsc.connect(bassGain).connect(ctx.destination);
    bassOsc.start(startTime);
    bassOsc.stop(startTime + beatDur);

    const melOsc = ctx.createOscillator();
    const melGain = ctx.createGain();
    melOsc.type = "sine";
    melOsc.frequency.value = melodyNotes[beat % melodyNotes.length];
    melGain.gain.setValueAtTime(0, startTime);
    melGain.gain.linearRampToValueAtTime(0.10, startTime + 0.03);
    melGain.gain.linearRampToValueAtTime(0.07, startTime + beatDur * 0.6);
    melGain.gain.linearRampToValueAtTime(0, startTime + beatDur * 0.95);
    melOsc.connect(melGain).connect(ctx.destination);
    melOsc.start(startTime);
    melOsc.stop(startTime + beatDur);
  }
}

/* ---------- Track B: "Autumn Leaves" style (Cm7–F7–BbMaj7–EbMaj7–Am7b5–D7–Gm) ---------- */
function buildImprovisation(ctx: OfflineAudioContext) {
  const bpm = 130;
  const beatDur = 60 / bpm;
  const chords = [
    [261.63, 311.13, 369.99], // Cm7
    [174.61, 220.00, 261.63], // F7
    [233.08, 293.66, 349.23], // BbMaj7
    [311.13, 392.00, 466.16], // EbMaj7
    [220.00, 261.63, 329.63], // Am7b5
    [293.66, 369.99, 440.00], // D7
    [196.00, 233.08, 293.66], // Gm
    [196.00, 233.08, 293.66], // Gm
  ];
  const melodyNotes = [
    523.25, 466.16, 415.30, 392.00,
    349.23, 311.13, 293.66, 261.63,
    293.66, 329.63, 349.23, 392.00,
    415.30, 392.00, 349.23, 293.66,
  ];
  const totalBeats = Math.floor(ctx.length / ctx.sampleRate / beatDur);

  for (let beat = 0; beat < totalBeats; beat++) {
    const chord = chords[Math.floor(beat / 4) % chords.length];
    const startTime = beat * beatDur;

    chord.forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.05, startTime + 0.08);
      gain.gain.linearRampToValueAtTime(0.03, startTime + beatDur * 0.85);
      gain.gain.linearRampToValueAtTime(0, startTime + beatDur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + beatDur + 0.05);
    });

    const bassOsc = ctx.createOscillator();
    const bassGain = ctx.createGain();
    bassOsc.type = "triangle";
    bassOsc.frequency.value = chord[0] / 2;
    bassGain.gain.setValueAtTime(0.13, startTime);
    bassGain.gain.exponentialRampToValueAtTime(0.01, startTime + beatDur * 0.7);
    bassOsc.connect(bassGain).connect(ctx.destination);
    bassOsc.start(startTime);
    bassOsc.stop(startTime + beatDur);

    const melOsc = ctx.createOscillator();
    const melGain = ctx.createGain();
    melOsc.type = "sine";
    melOsc.frequency.value = melodyNotes[beat % melodyNotes.length];
    melGain.gain.setValueAtTime(0, startTime);
    melGain.gain.linearRampToValueAtTime(0.09, startTime + 0.03);
    melGain.gain.linearRampToValueAtTime(0.06, startTime + beatDur * 0.5);
    melGain.gain.linearRampToValueAtTime(0, startTime + beatDur * 0.9);
    melOsc.connect(melGain).connect(ctx.destination);
    melOsc.start(startTime);
    melOsc.stop(startTime + beatDur);
  }
}

/* ---------- Track C: "Amazing Grace" style (G–C–G–Em–D–G) ---------- */
function buildFollowDirection(ctx: OfflineAudioContext) {
  const bpm = 72;
  const beatDur = 60 / bpm;
  const chords = [
    [196.00, 246.94, 293.66], // G
    [196.00, 246.94, 293.66], // G
    [261.63, 329.63, 392.00], // C
    [196.00, 246.94, 293.66], // G
    [164.81, 196.00, 246.94], // Em
    [146.83, 185.00, 220.00], // D
    [196.00, 246.94, 293.66], // G
    [196.00, 246.94, 293.66], // G
  ];
  const melodyNotes = [
    293.66, 392.00, 493.88, 392.00,
    493.88, 440.00, 392.00, 293.66,
    329.63, 293.66, 246.94, 293.66,
    392.00, 392.00, 293.66, 293.66,
  ];
  const totalBeats = Math.floor(ctx.length / ctx.sampleRate / beatDur);

  for (let beat = 0; beat < totalBeats; beat++) {
    const chord = chords[Math.floor(beat / 4) % chords.length];
    const startTime = beat * beatDur;

    chord.forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.055, startTime + 0.12);
      gain.gain.setValueAtTime(0.05, startTime + beatDur * 0.7);
      gain.gain.linearRampToValueAtTime(0, startTime + beatDur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + beatDur + 0.05);

      const harm = ctx.createOscillator();
      const harmGain = ctx.createGain();
      harm.type = "sine";
      harm.frequency.value = freq * 2;
      harmGain.gain.value = 0.015;
      harm.connect(harmGain).connect(ctx.destination);
      harm.start(startTime);
      harm.stop(startTime + beatDur + 0.05);
    });

    const bassOsc = ctx.createOscillator();
    const bassGain = ctx.createGain();
    bassOsc.type = "triangle";
    bassOsc.frequency.value = chord[0] / 2;
    bassGain.gain.setValueAtTime(0.10, startTime);
    bassGain.gain.exponentialRampToValueAtTime(0.01, startTime + beatDur * 0.9);
    bassOsc.connect(bassGain).connect(ctx.destination);
    bassOsc.start(startTime);
    bassOsc.stop(startTime + beatDur);

    const melOsc = ctx.createOscillator();
    const melGain = ctx.createGain();
    melOsc.type = "sine";
    melOsc.frequency.value = melodyNotes[beat % melodyNotes.length];
    melGain.gain.setValueAtTime(0, startTime);
    melGain.gain.linearRampToValueAtTime(0.11, startTime + 0.04);
    melGain.gain.setValueAtTime(0.08, startTime + beatDur * 0.6);
    melGain.gain.linearRampToValueAtTime(0, startTime + beatDur * 0.95);
    melOsc.connect(melGain).connect(ctx.destination);
    melOsc.start(startTime);
    melOsc.stop(startTime + beatDur);
  }
}

/* ---------- WAV encoder ---------- */
function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const numSamples = buffer.length;
  const dataSize = numSamples * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;

  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);

  // RIFF header
  writeString(view, 0, "RIFF");
  view.setUint32(4, totalSize - 8, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channels.push(buffer.getChannelData(ch));
  }

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }

  return arrayBuffer;
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/* ---------- Public API ---------- */

const builders: Record<TrackKey, (ctx: OfflineAudioContext) => void> = {
  lock_in: buildLockIn,
  improvisation: buildImprovisation,
  follow_direction: buildFollowDirection,
};

// Each track is ~50 seconds (looped by AudioRecorder)
const TRACK_DURATION = 50;

export async function getReferenceTrackUrl(key: TrackKey): Promise<string> {
  if (cache.has(key)) return cache.get(key)!;
  const blob = await renderOffline(TRACK_DURATION, builders[key]);
  const url = URL.createObjectURL(blob);
  cache.set(key, url);
  return url;
}
